import { FC, Suspense, useEffect, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { EIconSize } from 'utils/constants/app/ui';
import {
  competitions,
  events,
  gameList,
  markets,
  regions,
  sports,
  topMatchesWhere
} from 'utils/constants/sportsbook/data-fields';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { mountPoint } from 'utils/generic/route-manipulation';
import { isMobile } from 'utils/is-mobile';
import { getLivePreMatchSportTypes } from 'utils/sportsbook/sport-data';
import RidGenerator from 'utils/swarm/rid-generator';
import { ENotRealSports } from 'interfaces/not-real-sports';
import { Sport, SportOnly } from 'interfaces/sportsbook-data-levels';
import { executeCommand, unsubscribeCommand } from 'services/get-data-level';
import { TopMatchesCompactMenuSkeleton } from './Skeleton';
import { GlobalIcon } from 'components/GlobalIcon';
import { NavigationCollapse } from 'components/NavigationCollapse';
import { TopMatchesCompactLayout } from 'newcomponents/Shared/sportsbook/TopMatchesCompactLayout';
import { TopMatchesCompactLayoutSkeleton } from 'newcomponents/Shared/sportsbook/TopMatchesCompactLayout/skeleton';
import { TopMatchesSportList } from 'newcomponents/Shared/sportsbook/TopMatchesSportList';
import { useSportsbookConfigs } from 'hooks/sportsbook/useSportsbookConfigs';
import { useSbRouteMatch } from 'newhooks/generic/useSbRouteMatch';
import {
  setNavigationWrapperHeightChanged,
  setSwarmResponse
} from 'store/actions/new-sportsbook';
import { getSwarmResponse } from 'store/selectors/new-sportsbook';
import './index.less';

type TProps = {
  fromSportsList?: boolean;
};

export const TopMatchesCompactMenuItem: FC<TProps> = ({
  fromSportsList = false
}) => {
  const dispatch = useDispatch();
  const data = useSelector(getSwarmResponse)?.[ENotRealSports.topMatches];
  const history = useHistory();
  const { t } = useTranslation();
  const { params } = useSbRouteMatch();

  const { sportType, fixedScroll } = useSportsbookConfigs();

  const isTopMatches = params?.category === ENotRealSports.topMatches;

  useLayoutEffect(() => {
    const shouldSelectFirstCompetition =
      !isMobile() &&
      ((SpringConfigs.SPORT_MENU_DEFAULT_SELECTED ===
        ENotRealSports.topMatches &&
        !params.sport) ||
        params.category === ENotRealSports.topMatches);

    const hasTopLeagueData = data && !!Object.values(data).length;

    if (shouldSelectFirstCompetition) {
      if (hasTopLeagueData) {
        const { alias } = Object.values(data)[0];

        if (alias !== params.sport) {
          history.push(
            `${mountPoint(window.location.pathname)}/${
              ENotRealSports.topMatches
            }/${alias}`
          );
        }
      } else {
        history.push(
          `${mountPoint(window.location.pathname)}/${ENotRealSports.topMatches}`
        );
      }
    }
  }, [Object.values(data || {})[0]?.alias]);

  useEffect(() => {
    const rId = RidGenerator.gForSubscribe('TOP_MATCHES_COMPACT');

    const getData = (rid: string) => {
      const where = sportType
        ? {
            ...topMatchesWhere,
            sport: {
              type: {
                '@in': getLivePreMatchSportTypes(sportType)
              }
            }
          }
        : topMatchesWhere;

      const relations = isMobile()
        ? {
            ...sports,
            ...regions,
            competition: [...competitions.competition, 'favorite_order'],
            market: markets.market,
            event: events.event,
            game: gameList.game
          }
        : {
            ...sports,
            ...regions,
            competition: [...competitions.competition, 'favorite_order']
          };

      executeCommand(
        rid,
        relations,
        where,
        (data: { sport: Record<string, SportOnly | Sport> }) => {
          dispatch(
            setSwarmResponse({ [ENotRealSports.topMatches]: data.sport })
          );
        },
        true,
        (data: { sport: Record<string, SportOnly | Sport> }) => {
          dispatch(
            setSwarmResponse({ [ENotRealSports.topMatches]: data.sport })
          );
        }
      );
    };

    getData(rId);

    return () => {
      unsubscribeCommand(rId);
    };
  }, []);

  const onToggle = (active: boolean) => {
    if (!active && !isMobile() && !fixedScroll) {
      dispatch(setNavigationWrapperHeightChanged());
    }
  };

  if (isMobile()) {
    if (!data) {
      return <TopMatchesCompactMenuSkeleton />;
    }

    return (
      <Suspense fallback={<TopMatchesCompactLayoutSkeleton hideFirstIcon />}>
        <TopMatchesCompactLayout
          sportData={data}
          fromSportsList={fromSportsList}
        />
      </Suspense>
    );
  }

  return (
    <NavigationCollapse
      onChange={onToggle}
      key="topMatches"
      title={t('sportsbook.topMatches')}
      icon={
        <GlobalIcon
          lib="sports"
          name="topMatches"
          size={EIconSize._24}
          className="text-color"
        />
      }
      wrapperClassName="sportsList__sport topMatches__wrapper"
      headerClassName="sportsList__sport__header topMatches__header"
      defaultActive={
        SpringConfigs.SPORT_MENU_DEFAULT_SELECTED ===
          ENotRealSports.topMatches || isTopMatches
      }
    >
      <TopMatchesSportList />
    </NavigationCollapse>
  );
};
